import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Link,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import UndoIcon from '@mui/icons-material/Undo';
import PaymentsIcon from '@mui/icons-material/Payments';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import BillFlowList from '../../components/billflow/BillFlowList';
import StatusBadge from '../../components/billflow/StatusBadge';
import BillCollectDialog from '../projectmng/billcollect/BillCollectDialog';
import { taxKind, isForeign } from '../../components/billflow/BillFlowDetail';
import AskDialog from '../../components/common/dialog/AskDialog';
import {
  STATUS_META,
  STATUS_ORDER,
  BILL_STATUS,
  TODO_FILTER,
  TODO_META,
  filterBillRows,
  matchesTodo,
  deriveStatus,
  getAvailableActions,
} from '../../lib/billStatus';
import * as taxbillApi from '../../lib/api/taxbill';

const DATE_FIELD_OPTIONS = [
  { value: 'reqPlanDt', label: '발행예정일' },
  { value: 'reqCompDt', label: '발행일' },
  { value: 'procPlanDt', label: '수금/지급 예정일' },
  { value: 'procCompDt', label: '수금/지급일' },
  { value: 'rsltCompDt', label: '실적확정일' },
];

// 매출/매입 필터 - 상태/할 일 칩과 AND 로 조합된다.
const INV_TYP_OPTIONS = [
  { value: '', label: '전체' },
  { value: '매출', label: '매출(수금)' },
  { value: '매입', label: '매입(지급)' },
];

// 헤더 "수금" 뱃지에서 넘어오는 값. 뱃지는 예정+지연을 합쳐서 세므로 할 일 칩 둘 다 선택해 재현한다
// (뱃지는 매출만 세므로 매출 필터도 같이 선택한다. ForecastService#buildCollectCnt 참고).
const TODO_QUERY_COLLECT = 'collect';

const BillFlowListContainer = () => {
  // 결재를 쓰는 설정이면 발행요청 단계가 살아있다. 목록의 처리 메뉴도 같은 규칙을 따른다.
  const [approvalEnabled, setApprovalEnabled] = useState(false);
  const [searchParams] = useSearchParams();
  // 목록 자체는 서버에서 이미 가시성 스코프(부서/본부/전체)로 걸러져 온다.
  // "내 것"은 그 스코프 안에서 영업담당(salesUsrUuid)이 나인 행만 더 좁혀 보는 클라이언트 필터다.
  const { usrUuid } = useSelector(({ auth }) => ({ usrUuid: auth?.user?.usrUuid }));

  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // 상태 칩 - 복수 선택(배열). 빈 배열 = "전체".
  const [statusSelection, setStatusSelection] = useState([]);
  // 상태와 별개로 "해야 할 것" 관점 - 복수 선택(배열). 빈 배열 = 선택 없음.
  // 결재를 안 쓰면 요청 단계가 없어 상태만으로는 예정 건을 못 고른다.
  const [todoSelection, setTodoSelection] = useState([]);
  // 매출/매입 - 단일 선택('' = 전체). 상태/할 일 칩과는 AND 로 조합된다.
  const [invTypFilter, setInvTypFilter] = useState('');
  // "내 것" 토글. 기본은 꺼짐(=내 스코프 전체) - 단, 헤더 수금 뱃지(?todo=collect)로 들어오면
  // 뱃지와 같은 기준(나의 매출 세금계산서)으로 보여야 해서 기본값을 켬으로 시작한다.
  const [onlyMine, setOnlyMine] = useState(false);
  // 목록에서 바로 여는 수금 내역 대상
  const [collectTarget, setCollectTarget] = useState(null);
  // 진행할 액션 { row, action }
  const [actionTarget, setActionTarget] = useState(null);

  // 날짜 검색
  const [dateField, setDateField] = useState('reqPlanDt');
  // 기본 조회범위 = 최근 1년 (오늘-1년 ~ 오늘)
  const [strtDt, setStrtDt] = useState(() => dayjs().subtract(1, 'year'));
  const [endDt, setEndDt] = useState(() => dayjs());

  const buildParams = useCallback(() => {
    const p = { invYn: 'Y', dateField };
    if (strtDt && endDt) {
      p.strtDt = dayjs(strtDt).format('YYYY-MM-DD');
      p.endDt = dayjs(endDt).format('YYYY-MM-DD');
    }
    return p;
  }, [dateField, strtDt, endDt]);

  const load = useCallback(
    async (params) => {
      setLoading(true);
      try {
        const res = await taxbillApi.getTaxbills(params);
        setRows(res.data?.list || []);
      } catch (err) {
        const message =
          typeof err.response !== 'undefined'
            ? err.response.data.message
            : err.message;
        enqueueSnackbar(message, { variant: 'error' });
      }
      setLoading(false);
    },
    [enqueueSnackbar],
  );

  // 최초 1회 로드 (발행예정일 기준, 최근 1년).
  // 헤더 "수금" 뱃지(?todo=collect)에서 들어온 경우는 수금예정일 기준으로 넓게 조회한다 -
  // 기본 조회창(발행예정일 기준 최근 1년)엔 수금예정/지연 건이 범위 밖일 수 있어서다.
  useEffect(() => {
    if (searchParams.get('todo') === TODO_QUERY_COLLECT) {
      const s = dayjs().subtract(2, 'year');
      const e = dayjs().add(1, 'year');
      setDateField('procPlanDt');
      setStrtDt(s);
      setEndDt(e);
      // 뱃지(예정+지연 합산)를 그대로 재현하려면 두 할 일 칩을 같이 선택한다.
      setTodoSelection([TODO_FILTER.COLLECT_DUE, TODO_FILTER.COLLECT_OVERDUE]);
      // 뱃지 숫자(ForecastService#buildCollectCnt)는 "나의 매출" 수금건만 센다 -
      // 매입은 "지급"이라 성격이 달라 뱃지 집계에서 빠진다. 뱃지를 눌러 들어온 화면이
      // 더 많은 건을 보여주면 숫자가 안 맞다고 느껴지므로 매출 필터와 내 것을 같이 켠다.
      setInvTypFilter('매출');
      setOnlyMine(true);
      load({
        invYn: 'Y',
        dateField: 'procPlanDt',
        strtDt: s.format('YYYY-MM-DD'),
        endDt: e.format('YYYY-MM-DD'),
      });
      return;
    }
    load(buildParams());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    taxbillApi.getIssueFlowConfig()
      .then((res) => setApprovalEnabled(Boolean(res?.data?.approvalEnabled)))
      // 설정을 못 읽으면 결재 미사용으로 본다.
      .catch(() => setApprovalEnabled(false));
  }, []);

  const onSearch = () => load(buildParams());
  const onResetSearch = () => {
    const s = dayjs().subtract(1, 'year');
    const e = dayjs();
    setDateField('reqPlanDt');
    setStrtDt(s);
    setEndDt(e);
    // 초기화는 "내 스코프 전체"로 되돌린다. 뱃지로 들어왔을 때 켜졌던 내 것/매출 필터도 같이 끈다.
    setOnlyMine(false);
    setInvTypFilter('');
    load({
      invYn: 'Y',
      dateField: 'reqPlanDt',
      strtDt: s.format('YYYY-MM-DD'),
      endDt: e.format('YYYY-MM-DD'),
    });
  };

  const requestAction = (row, action) => (event) => {
    event.stopPropagation();
    setActionTarget({ row, action });
  };

  const onClose = () => setActionTarget(null);

  const onConfirm = async () => {
    if (!actionTarget) return;
    const { row, action } = actionTarget;
    const body = { invUuid: row.invUuid, ...action.payload };
    if (action.dateField) {
      body[action.dateField] = dayjs().format('YYYY-MM-DD');
    }
    try {
      const res = await taxbillApi.editTaxbill(body);
      if (res.data) {
        enqueueSnackbar(`${action.label} 완료`, { variant: 'success' });
        await load(buildParams());
      }
    } catch (err) {
      const message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, { variant: 'error' });
    }
    setActionTarget(null);
  };

  // 상태별 건수 (다른 필터와 무관하게 전체 rows 기준 - 전부터의 동작과 동일)
  const counts = useMemo(() => {
    const c = { ALL: rows.length };
    rows.forEach((r) => {
      const s = deriveStatus(r);
      c[s] = (c[s] || 0) + 1;
    });
    return c;
  }, [rows]);

  // 매출/매입 칩 건수도 전체 rows 기준.
  const invTypCounts = useMemo(() => {
    const c = { ALL: rows.length, '매출': 0, '매입': 0 };
    rows.forEach((r) => {
      if (r.invTyp === '매출' || r.invTyp === '매입') {
        c[r.invTyp] += 1;
      }
    });
    return c;
  }, [rows]);

  const filteredRows = useMemo(() => {
    const today = dayjs().format('YYYY-MM-DD');
    // 상태 줄 OR / 할 일 줄 OR, 그 둘과 매출·매입 필터는 AND (lib/billStatus.js filterBillRows).
    let list = filterBillRows(rows, {
      statusSet: statusSelection,
      todoSet: todoSelection,
      invTyp: invTypFilter,
      today,
    });
    if (onlyMine) {
      // 서버가 이미 가시성 스코프로 거른 rows 안에서, 영업담당(salesUsrUuid)이 나인 것만 더 좁힌다.
      list = list.filter((r) => r.salesUsrUuid === usrUuid);
    }
    return list;
  }, [rows, statusSelection, todoSelection, invTypFilter, onlyMine, usrUuid]);

  // 결재를 안 쓰면 요청 단계 자체가 만들어지지 않는다. 항상 0인 칩은 자리만 차지한다.
  const visibleStatuses = useMemo(
    () =>
      approvalEnabled
        ? STATUS_ORDER
        : STATUS_ORDER.filter(
            (s) => s !== BILL_STATUS.ISSUE_REQ && s !== BILL_STATUS.COLLECT_REQ,
          ),
    [approvalEnabled],
  );

  // 칩에 건수를 같이 보여준다. 0이면 눌러볼 필요가 없다는 것도 정보다.
  const todoCounts = useMemo(() => {
    const today = dayjs().format('YYYY-MM-DD');
    const acc = {};
    Object.values(TODO_FILTER).forEach((k) => {
      acc[k] = rows.filter((r) => matchesTodo(r, k, today)).length;
    });
    return acc;
  }, [rows]);

  const columns = useMemo(
    () => [
      {
        field: 'status',
        headerName: '상태',
        width: 130,
        sortable: false,
        renderCell: (params) => <StatusBadge bill={params.row} />,
      },
      {
        field: 'invNo',
        headerName: '계산서번호',
        minWidth: 190,
        flex: 1,
        renderCell: (params) => (
          <Link
            underline="none"
            component={RouterLink}
            to={`/billflow/${params.row.invUuid}`}
          >
            {params.value}
          </Link>
        ),
      },
      { field: 'partnerNm', headerName: '고객명', minWidth: 160, flex: 1 },
      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 160,
        flex: 1,
        renderCell: (params) => {
          const r = params.row;
          if (!r.prjUuid) return params.value || '';
          // prjTyp N=프로젝트→projectmng, 그 외(M=유지보수)→maintancetmng
          const base = r.prjTyp === 'N' ? 'projectmng' : 'maintancetmng';
          const grade = r.cmpGrade || '1';
          return (
            <Link
              underline="none"
              component={RouterLink}
              to={`/${base}/${r.prjUuid}/${grade}/1`}
            >
              {params.value}
            </Link>
          );
        },
      },
      { field: 'invTyp', headerName: '유형', width: 90, align: 'center', headerAlign: 'center' },
      {
        field: 'taxKind',
        headerName: '종류',
        width: 120,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const kind = taxKind(params.row);
          return (
            <Stack direction="row" spacing={0.5} justifyContent="center">
              {kind === '-' ? (
                '-'
              ) : (
                <Chip
                  size="small"
                  variant="outlined"
                  color={kind === '과세' ? 'default' : 'info'}
                  label={kind}
                />
              )}
              {isForeign(params.row) && (
                <Chip size="small" variant="outlined" color="warning" label="외환" />
              )}
            </Stack>
          );
        },
      },
      {
        field: 'invAmt',
        headerName: '계산서 금액',
        width: 140,
        type: 'number',
        // MUI X v7: valueGetter(value, row) — 첫 인자가 셀 값(row.invAmt)
        valueGetter: (value) => Number(value || 0),
      },
      { field: 'salesUsrNm', headerName: '영업담당', width: 110 },
      {
        field: 'actions',
        type: 'actions',
        headerName: '처리',
        width: 90,
        getActions: (params) => [
          // 부분 수금은 목록에서도 바로 들어갈 수 있어야 한다.
          // 발행이 끝난 건에만 의미가 있다.
          ...(params.row.reqCompYn === 'Y'
            ? [
                <GridActionsCellItem
                  key="collect"
                  icon={<PaymentsIcon />}
                  label={params.row.invTyp === '매입' ? '지급 내역' : '수금 내역'}
                  onClick={() => setCollectTarget({
                    invUuid: params.row.invUuid,
                    invTyp: params.row.invTyp,
                  })}
                  showInMenu
                />,
              ]
            : []),
          ...getAvailableActions(params.row, { approvalEnabled }).map((action) => (
            <GridActionsCellItem
              key={action.key}
              icon={action.kind === 'cancel' ? <UndoIcon /> : <ArrowForwardIcon />}
              label={action.label}
              onClick={requestAction(params.row, action)}
              color={action.kind === 'cancel' ? 'warning' : 'primary'}
              showInMenu
            />
          )),
        ],
      },
    ],
    // 결재 설정은 비동기로 들어온다. 의존성에서 빠지면 처음 값(미사용)으로 굳어져
    // 나중에 결재를 켜도 목록의 처리 메뉴가 안 바뀐다.
    [approvalEnabled],
  );

  const action = actionTarget?.action;

  return (
    <>
      <BreadcrumbsContainer
        title="세금계산서 관리/세금계산서 흐름(new)"
        icon="pay"
      />
      <Box sx={{ px: 2, pt: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <TextField
            select
            size="small"
            label="날짜 기준"
            value={dateField}
            onChange={(e) => setDateField(e.target.value)}
            sx={{ minWidth: 130 }}
          >
            {DATE_FIELD_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
            <DatePicker
              label="시작일"
              format="YYYY-MM-DD"
              value={strtDt}
              onChange={setStrtDt}
              slotProps={{ textField: { size: 'small' } }}
            />
            <DatePicker
              label="종료일"
              format="YYYY-MM-DD"
              value={endDt}
              onChange={setEndDt}
              slotProps={{ textField: { size: 'small' } }}
            />
          </LocalizationProvider>
          <Button variant="contained" onClick={onSearch}>
            검색
          </Button>
          <Button variant="outlined" onClick={onResetSearch}>
            초기화
          </Button>
          {/* 내 스코프(부서/본부/전체) 안에서 영업담당이 나인 것만 더 좁혀 보는 토글. 서버 재조회 없이 즉시 적용된다. */}
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={onlyMine}
                onChange={(e) => setOnlyMine(e.target.checked)}
              />
            }
            label="내 것"
          />
        </Stack>
      </Box>
      {/* 상태 / 할 일 / 매출·매입을 한 줄에 두고 구분선으로만 나눈다.
          줄을 나누면 필터 영역이 여러 줄이 되어 목록이 밀린다.
          건수 0인 칩은 흐리게 — 눌러도 빈 목록이라 시선을 뺏을 이유가 없다.
          상태·할 일은 각각 복수 선택(줄 안 OR) 이고, 매출/매입은 단일 선택이다.
          세 묶음은 서로 AND 로 조합된다(lib/billStatus.js filterBillRows). */}
      <Box sx={{ px: 2, py: 1 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={`전체 ${counts.ALL || 0}`}
            color={statusSelection.length === 0 ? 'primary' : 'default'}
            variant={statusSelection.length === 0 ? 'filled' : 'outlined'}
            onClick={() => setStatusSelection([])}
          />
          {visibleStatuses.map((s) => {
            const n = counts[s] || 0;
            const active = statusSelection.includes(s);
            return (
              <Chip
                key={s}
                size="small"
                label={`${STATUS_META[s].label} ${n}`}
                color={active ? 'primary' : 'default'}
                variant={active ? 'filled' : 'outlined'}
                onClick={() =>
                  setStatusSelection((prev) =>
                    prev.includes(s) ? prev.filter((v) => v !== s) : [...prev, s],
                  )
                }
                sx={n === 0 && !active ? { opacity: 0.45 } : undefined}
              />
            );
          })}

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          {Object.values(TODO_FILTER).map((k) => {
            const n = todoCounts[k] || 0;
            const active = todoSelection.includes(k);
            return (
              <Chip
                key={k}
                size="small"
                label={`${TODO_META[k].label} ${n}`}
                color={active ? TODO_META[k].color : 'default'}
                variant={active ? 'filled' : 'outlined'}
                onClick={() =>
                  setTodoSelection((prev) =>
                    prev.includes(k) ? prev.filter((v) => v !== k) : [...prev, k],
                  )
                }
                sx={n === 0 && !active ? { opacity: 0.45 } : undefined}
              />
            );
          })}
          {todoSelection.length > 0 && (
            <Button size="small" onClick={() => setTodoSelection([])}>해제</Button>
          )}

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          {INV_TYP_OPTIONS.map((o) => {
            const n = o.value ? invTypCounts[o.value] || 0 : invTypCounts.ALL || 0;
            const active = invTypFilter === o.value;
            return (
              <Chip
                key={o.label}
                size="small"
                label={`${o.label} ${n}`}
                color={active ? 'primary' : 'default'}
                variant={active ? 'filled' : 'outlined'}
                onClick={() => setInvTypFilter(active ? '' : o.value)}
                sx={n === 0 && !active && o.value ? { opacity: 0.45 } : undefined}
              />
            );
          })}
        </Stack>
      </Box>
      <BillFlowList columns={columns} rows={filteredRows} loading={loading} />
      <BillCollectDialog
        open={Boolean(collectTarget)}
        invUuid={collectTarget?.invUuid}
        invTyp={collectTarget?.invTyp}
        onClose={() => setCollectTarget(null)}
        // 수금이 등록되면 수금완료 상태가 파생으로 바뀐다. 목록도 다시 읽는다.
        onSaved={() => load(buildParams())}
      />
      <AskDialog
        visible={!!actionTarget}
        title={action ? action.label : ''}
        description={
          action
            ? action.cascade
              ? `"${action.label}" 처리 시 이후 단계까지 함께 초기화됩니다.\n계속하시겠습니까?`
              : `"${action.label}" 처리하시겠습니까?`
            : ''
        }
        confirmText={action?.kind === 'cancel' ? '취소 처리' : '처리'}
        cancelText="닫기"
        onConfirm={onConfirm}
        onCancle={onClose}
      />
    </>
  );
};

export default BillFlowListContainer;
